/**
 * A board row's four day-plan cells (moved out of ContainerRowCells,
 * 23/10/2026): Today's list (every drop's list in send order, every group
 * together, 26/10/2026), List group (each group's chip opens its pop-up),
 * Progress and State — the day as it really stands, the Live
 * card's facts; "not today" when the container does not run on the run
 * date's weekday. Until the plan answers PendingCells stands in for them.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { describeRunDays, runsOn } from '@trafficpulse/shared';

import { Badge } from '@/components/ui';

import type { DayPlanContainerRow, DropGenContainerSummary } from '../../../drop-generator.types';
import type { ClockCtx } from '../../../utils/clock/dk-label';
import { deriveContainerLive } from '../../live/live-status';
import { type CapCuts } from '../../shared/cap/cap-cuts';
import { ContainerDayBadge, type LiveAutoSend } from '../../shared/StateBadges';
import { boardListRows } from '../board-list-rows';
import { TodayListsCell } from '../lists/TodayListsCell';
import { GroupCell } from './GroupCell';

/** "3/7 · 3 auto" once the day's drafts exist, "7 planned" before. */
const progressLine = (facts: ReturnType<typeof deriveContainerLive> | null, preview: DayPlanContainerRow | undefined): string => {
  if (!facts || !preview) return '—';
  if (facts.state === 'not-generated') return `${preview.totals.drops} planned`;
  return `${facts.sent}/${facts.planned}${facts.auto > 0 ? ` · ${facts.auto} auto` : ''}`;
};

interface Props {
  container: DropGenContainerSummary;
  preview: DayPlanContainerRow | undefined;
  clock: ClockCtx;
  cuts: CapCuts;
  autoSend: LiveAutoSend;
  /** The day plan's run date (recipient-local); null = unknown. */
  runDate: string | null;
}

export const PlanCells: FC<Props> = ({ container, preview, clock, cuts, autoSend, runDate }) => {
  const facts = preview ? deriveContainerLive(preview) : null;
  const groups = boardListRows(preview);
  const days = describeRunDays(container.runDays);
  const offToday = runDate !== null && !runsOn(container.runDays, runDate);
  return (
    <>
      <TodayListsCell groups={groups} clock={clock} slots={container.slots ?? []} />
      <GroupCell groups={groups} linked={container.groups ?? []} campaignWiserId={container.campaignWiserId} />
      <span className="flex min-w-0 flex-col gap-0.5 whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
        <span>{progressLine(facts, preview)}</span>
        {facts?.scheduled ? <span className="text-[10px]">{facts.scheduled} scheduled</span> : null}
        {cuts.cut.size > 0 ? ( // 20/10/2026 — the campaign group's daily SMS cap cut these slots; the editor's Drop slots says which
          <span className="text-[10px] text-warning" title="Slots the campaign group's daily SMS cap cut today — open the container's Drop slots to see which, and Edit the cap">
            {cuts.cut.size} cut by cap
          </span>
        ) : null}
      </span>
      <span>
        {!container.enabled ? (
          <Badge tone="secondary">disabled</Badge>
        ) : offToday ? (
          <span title={`Runs ${days} — not on this weekday. Skipped like a switched-off container.`}>
            <Badge tone="secondary">not today</Badge>
          </span>
        ) : facts && preview && preview.drops.length > 0 ? (
          <ContainerDayBadge facts={facts} autoSend={autoSend} />
        ) : (
          <Badge tone="secondary">nothing planned</Badge>
        )}
      </span>
    </>
  );
};
